<template>
    <div class="bg-base-200 h-screen-header rounded-xl w-full xl:w-screen-sidebar pb-20 md:pb-0">
        <div class="grid grid-cols-1 lg:grid-cols-12 gap-2 md:gap-3 h-full p-2 md:p-3">
            <div class="lg:col-span-8 bg-base-100 rounded-xl md:rounded-2xl shadow-xl p-2 md:p-3 overflow-hidden flex flex-col">
                <div class="flex items-center justify-between mb-2 flex-wrap gap-2">
                    <h1 class="text-lg md:text-xl font-semibold">{{ $tt('多端同播') }}</h1>
                    <div class="flex items-center text-xs text-base-content/70 gap-2">
                        <span v-if="syncRoomId">{{ $tt('房间') }}: {{ syncRoomId }}</span>
                        <span v-if="syncRoomId">{{ $tt('在线') }}: {{ syncOnlineCount }}</span>
                    </div>
                </div>

                <div class="flex items-center flex-wrap gap-1.5 md:gap-2 mb-2 md:mb-3">
                    <button class="btn btn-xs text-xs" @click="createSyncRoom" :disabled="syncBusy">{{ $tt('创建房间') }}</button>
                    <input v-model.trim="syncRoomInput" class="input input-bordered input-xs w-20 md:w-28 text-xs" :placeholder="$tt('房间号')" />
                    <button class="btn btn-xs text-xs" @click="joinSyncRoom" :disabled="syncBusy || !syncRoomInput">{{ $tt('加入') }}</button>
                    <button class="btn btn-xs text-xs" @click="leaveSyncRoom" :disabled="syncBusy || !syncRoomId">{{ $tt('离开') }}</button>
                    <span class="text-xs text-orange-500" v-if="syncReadonly">{{ $tt('仅房主可控') }}</span>
                </div>

                <div
                    v-if="showOwnerReconnectTip"
                    class="mb-2 md:mb-3 rounded-lg md:rounded-xl border border-amber-300 bg-amber-100/80 px-2 md:px-3 py-1.5 md:py-2 text-xs text-amber-700"
                >
                    {{ $tt('房主连接中，等待重连...若持续掉线会自动解散会话') }}
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3 flex-1 min-h-0">
                    <div class="bg-base-200 rounded-lg md:rounded-xl p-2 md:p-3 overflow-hidden flex flex-col">
                        <div class="text-xs md:text-sm font-medium mb-1.5 md:mb-2">{{ $tt('滚动歌词') }}</div>
                        <div ref="lyricContainer" class="overflow-y-auto flex-1">
                            <ul ref="lyricUL">
                                <li v-for="(item, i) in lyricsObjArr"
                                    :key="item.uid"
                                    :data-index="i"
                                    ref="lyric"
                                    @click="scrollToLyric(i)"
                                    :class="[
                                        lyricIndex === i ? 'text-primary text-base md:text-lg' : 'text-base-content/70 text-sm md:text-base',
                                        'mb-1.5 md:mb-2 text-center cursor-pointer transition-all'
                                    ]">
                                    {{ item.lyric }}
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div class="bg-base-200 rounded-lg md:rounded-xl p-2 md:p-3 overflow-hidden flex flex-col">
                        <div class="text-xs md:text-sm font-medium mb-1.5 md:mb-2">{{ $tt('播放列表') }}</div>
                        <div class="overflow-y-auto flex-1">
                            <TrackList :tracks="tracks" no-artist :index-field="'index'" :show-image="true" :readonly="syncReadonly" />
                        </div>
                    </div>
                </div>
            </div>

            <div class="lg:col-span-4 flex flex-col gap-2 md:gap-3 min-h-0 mb-safe">
                <div class="bg-base-100 rounded-xl md:rounded-2xl shadow-xl p-2 md:p-3 flex-shrink-0">
                    <div class="flex items-center justify-between mb-1.5 md:mb-2">
                        <div class="text-xs md:text-sm font-medium">{{ $tt('房间成员') }}</div>
                        <button
                            v-if="onlineUsers.length > 0"
                            class="btn btn-ghost btn-xs text-xs"
                            @click="toggleMembersCollapse"
                        >
                            <span>{{ membersCollapsed ? $tt('展开') : $tt('收起') }}</span>
                            <span class="badge badge-outline badge-xs ml-1">{{ syncOnlineCount }}</span>
                        </button>
                    </div>
                    <div
                        v-if="membersCollapsed"
                        class="rounded-lg md:rounded-xl border border-base-300 bg-base-200/70 px-2 md:px-3 py-1.5 md:py-2 text-xs text-base-content/70"
                    >
                        <div>{{ $tt('在线') }} {{ syncOnlineCount }} {{ $tt('人') }}</div>
                        <div v-if="ownerUser" class="mt-1 truncate">{{ $tt('房主') }}: {{ ownerUser.username }}</div>
                        <div v-else class="mt-1 truncate text-amber-600">{{ $tt('房主暂时离线') }}</div>
                    </div>
                    <div v-else class="max-h-24 md:max-h-28 lg:max-h-32 overflow-y-auto pr-1 space-y-1.5 md:space-y-2">
                        <div
                            v-for="u in onlineUsers"
                            :key="u.user_id"
                            class="flex items-center gap-2 md:gap-3 px-2 md:px-3 py-1.5 md:py-2 rounded-xl md:rounded-2xl border border-base-300 bg-base-200/70"
                        >
                            <div class="w-7 h-7 md:w-8 md:h-8 rounded-full text-white font-bold text-xs md:text-sm flex items-center justify-center shadow-md select-none" :style="{ background: avatarGradient(u.username) }">
                                {{ getUserInitial(u.username) }}
                            </div>
                            <div class="min-w-0 flex-1">
                                <div class="text-xs md:text-sm font-semibold truncate">{{ u.username }}</div>
                                <div class="text-[10px] md:text-[11px] text-base-content/60">
                                    {{ isSameId(u.user_id, syncOwnerUserId)
                                        ? (syncOwnerOnline ? $tt('房主') : $tt('房主(重连中)'))
                                        : $tt('成员') }}
                                </div>
                            </div>
                            <span v-if="isSameId(u.user_id, syncCurrentUserId)" class="badge badge-outline badge-xs text-[10px]">{{ $tt('我') }}</span>
                        </div>
                        <div v-if="onlineUsers.length === 0" class="text-xs text-base-content/60 text-center py-2">{{ $tt('暂无成员') }}</div>
                    </div>
                </div>

                <div class="bg-base-100 rounded-xl md:rounded-2xl shadow-xl p-2 md:p-3 flex-1 min-h-[200px] md:min-h-0 flex flex-col">
                    <div class="text-xs md:text-sm font-medium mb-1.5 md:mb-2">{{ $tt('聊天') }}</div>
                    <div ref="chatScroll" class="flex-1 overflow-y-auto pr-1 rounded-lg md:rounded-2xl border border-base-300 bg-base-200/70 p-2 md:p-3 min-h-[150px]">
                        <div
                            v-for="msg in chatMessages"
                            :key="msg.id"
                        >
                            <div
                                v-if="msg.type === 'system'"
                                class="my-1.5 md:my-2 text-center text-[10px] md:text-[11px] text-base-content/60"
                            >
                                <span class="inline-block px-2 py-1 rounded-full bg-base-300/70">{{ msg.message }}</span>
                            </div>
                            <div
                                v-else
                                :class="['flex flex-col mb-1.5 md:mb-2', isMine(msg) ? 'items-end' : 'items-start']"
                            >
                                <div class="flex items-center gap-1.5 md:gap-2 mb-0.5 md:mb-1 text-[10px] md:text-[11px] text-base-content/60">
                                    <span class="font-semibold">{{ msg.username }}</span>
                                    <span>{{ formatChatTime(msg.created_at_ms) }}</span>
                                </div>
                                <div :class="[
                                    'max-w-[85%] md:max-w-[82%] px-2.5 md:px-3 py-1.5 md:py-2 text-xs md:text-sm leading-5 break-words whitespace-pre-wrap rounded-xl md:rounded-2xl shadow-sm',
                                    isMine(msg)
                                        ? 'bg-primary text-primary-content rounded-br-sm'
                                        : 'bg-base-100 text-base-content border border-base-300 rounded-bl-sm'
                                ]">
                                    {{ msg.message }}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="mt-1.5 md:mt-2 flex gap-1.5 md:gap-2">
                        <input
                            v-model.trim="chatInput"
                            class="input input-bordered input-xs md:input-sm flex-1 text-xs md:text-sm"
                            :placeholder="$tt('输入消息')"
                            @keyup.enter="sendChat"
                            :disabled="!syncRoomId"
                        />
                        <button class="btn btn-xs md:btn-sm text-xs md:text-sm" @click="sendChat" :disabled="!syncRoomId || !chatInput">{{ $tt('发送') }}</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import TrackList from '@/views/library/track/TrackList'
import { mapGetters } from 'vuex'

const AVATAR_COLORS = [
    ['#38bdf8', '#0284c7'],
    ['#34d399', '#059669'],
    ['#22d3ee', '#0e7490'],
    ['#a78bfa', '#7c3aed'],
    ['#fb7185', '#e11d48'],
    ['#4ade80', '#15803d'],
]

const MEMBER_COLLAPSE_THRESHOLD = 6

export default {
    name: 'GroupCast',
    components: { TrackList },
    data() {
        return {
            syncRoomInput: '',
            syncBusy: false,
            lyricIndex: 0,
            lyricsObjArr: [],
            chatInput: '',
            chatMessages: [],
            membersSnapshotReady: false,
            membersCollapsed: false,
            membersManualToggled: false,
        }
    },
    computed: {
        ...mapGetters(['trackId']),
        tracks() {
            return this.$store.state.common.queue || []
        },
        queueIndex() {
            return this.$store.state.common.queueIndex
        },
        currentTime() {
            return this.$store.state.common.currentTime
        },
        duration() {
            return this.$store.state.common.duration
        },
        syncRoomId() {
            return this.$store.state.syncRoom.roomId
        },
        syncOnlineCount() {
            return this.$store.state.syncRoom.onlineCount
        },
        syncIsOwner() {
            return this.$store.getters['syncRoom/isOwner']
        },
        syncReadonly() {
            return !!this.syncRoomId && !this.syncIsOwner
        },
        syncOwnerUserId() {
            return this.$store.state.syncRoom.ownerUserId
        },
        syncOwnerOnline() {
            return this.$store.state.syncRoom.ownerOnline !== false
        },
        syncCurrentUserId() {
            return this.$store.state.syncRoom.currentUserId
        },
        onlineUsers() {
            return this.$store.state.syncRoom.onlineUsers || []
        },
        ownerUser() {
            return this.onlineUsers.find((u) => this.isSameId(u.user_id, this.syncOwnerUserId)) || null
        },
        showOwnerReconnectTip() {
            return !!this.syncRoomId && !this.syncIsOwner && !this.syncOwnerOnline
        },
    },
    created() {
        this.loadCurrentLyrics()
    },
    watch: {
        trackId() {
            this.loadCurrentLyrics()
        },
        currentTime(newTime) {
            if (!this.lyricsObjArr.length) return
            let activeIndex = -1
            for (let i = 0; i < this.lyricsObjArr.length; i += 1) {
                const t = parseFloat(this.lyricsObjArr[i].time)
                if (Number.isNaN(t)) continue
                if (newTime >= t) {
                    activeIndex = i
                } else {
                    break
                }
            }
            if (activeIndex >= 0 && activeIndex !== this.lyricIndex) {
                this.lyricIndex = activeIndex
            }
        },
        lyricIndex(newIndex) {
            this.scrollActiveLyricIntoView(newIndex)
        },
        '$store.state.syncRoom.lastChatMessage': {
            handler(msg) {
                if (!msg) return

                if (msg.type === 'system') {
                    const fallbackId = msg.event === 'playback_action'
                        ? `chat_pb_${msg.created_at_ms}_${msg.action_type || 'event'}`
                        : `${msg.created_at_ms}_${Math.random()}`
                    this.chatMessages.push({
                        ...msg,
                        id: msg.version ? `chat_pb_${msg.version}` : fallbackId,
                        type: 'system',
                    })
                } else {
                    this.chatMessages.push({
                        ...msg,
                        id: `${msg.created_at_ms}_${Math.random()}`,
                        type: 'user',
                    })
                }
                this.scrollChatToBottom()
            },
        },
        syncRoomId(newVal) {
            if (!newVal) {
                this.chatMessages = []
                this.membersSnapshotReady = false
                this.membersCollapsed = false
                this.membersManualToggled = false
            }
        },
        onlineUsers(newUsers = [], oldUsers = []) {
            if (!this.syncRoomId) return
            if (!this.membersSnapshotReady) {
                this.membersSnapshotReady = true
                if (!this.membersManualToggled) {
                    this.membersCollapsed = newUsers.length > MEMBER_COLLAPSE_THRESHOLD
                }
                return
            }

            if (!this.membersManualToggled) {
                this.membersCollapsed = newUsers.length > MEMBER_COLLAPSE_THRESHOLD
            }

            const oldMap = new Map(oldUsers.map((u) => [String(u.user_id), u]))
            const newMap = new Map(newUsers.map((u) => [String(u.user_id), u]))
            const ownerId = String(this.syncOwnerUserId || '')

            newUsers.forEach((u) => {
                const userId = String(u.user_id)
                if (!oldMap.has(userId)) {
                    if (userId === ownerId) {
                        this.pushSystemMessage(`${u.username} ${this.$tt('已重连房间')}`)
                    } else {
                        this.pushSystemMessage(`${u.username} ${this.$tt('加入房间')}`)
                    }
                }
            })

            oldUsers.forEach((u) => {
                const userId = String(u.user_id)
                if (!newMap.has(userId)) {
                    if (userId === ownerId) {
                        this.pushSystemMessage(`${u.username} ${this.$tt('连接中断，等待重连')}`)
                    } else {
                        this.pushSystemMessage(`${u.username} ${this.$tt('离开房间')}`)
                    }
                }
            })
        },
    },
    methods: {
        toggleMembersCollapse() {
            this.membersCollapsed = !this.membersCollapsed
            this.membersManualToggled = true
        },
        isSameId(left, right) {
            if (left === null || left === undefined || right === null || right === undefined) {
                return false
            }
            return String(left) === String(right)
        },
        isMine(msg) {
            return this.isSameId(msg.user_id, this.syncCurrentUserId)
        },
        scrollChatToBottom() {
            this.$nextTick(() => {
                const chatBox = this.$refs.chatScroll
                if (chatBox) chatBox.scrollTop = chatBox.scrollHeight
            })
        },
        pushSystemMessage(text, createdAtMs = Date.now(), customId = '') {
            this.chatMessages.push({
                id: customId || `sys_${createdAtMs}_${Math.random()}`,
                type: 'system',
                message: text,
                created_at_ms: createdAtMs,
            })
            this.scrollChatToBottom()
        },
        getUserInitial(username) {
            const text = (username || '').trim()
            return text ? text.slice(0, 1).toUpperCase() : '?'
        },
        avatarGradient(username) {
            const text = (username || '').trim()
            let hash = 0
            for (let i = 0; i < text.length; i += 1) {
                hash = text.charCodeAt(i) + ((hash << 5) - hash)
            }
            const colors = AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
            return `linear-gradient(135deg, ${colors[0]}, ${colors[1]})`
        },
        formatChatTime(ms) {
            if (!ms) return ''
            const date = new Date(ms)
            const h = `${date.getHours()}`.padStart(2, '0')
            const m = `${date.getMinutes()}`.padStart(2, '0')
            return `${h}:${m}`
        },
        async loadCurrentLyrics() {
            if (!this.trackId || this.trackId === -1) return
            try {
                const res = await this.$api.subsonic.getLyrics(this.trackId)
                this.lyricsObjArr = this.formatLyrics(res.value)
            } catch (e) {
                this.lyricsObjArr = []
            }
        },
        async createSyncRoom() {
            if (this.syncBusy) return
            this.syncBusy = true
            try {
                const customRoomId = (this.syncRoomInput || '').trim()
                const data = await this.$store.dispatch('syncRoom/createRoom', {
                    name: '',
                    room_id: customRoomId || undefined,
                })
                this.syncRoomInput = data.room.room_id
                this.$myMsg.notify({ title: this.$tt('创建成功'), content: `${this.$tt('房间号')}: ${data.room.room_id}`, type: 'success', time: 1500 })
            } catch (error) {
                this.$myMsg.notify({ title: this.$tt('创建失败'), content: error.message || this.$tt('创建房间失败'), type: 'error', time: 2000 })
            } finally {
                this.syncBusy = false
            }
        },
        async joinSyncRoom() {
            if (!this.syncRoomInput || this.syncBusy) return
            this.syncBusy = true
            try {
                await this.$store.dispatch('syncRoom/joinRoom', this.syncRoomInput)
                this.$myMsg.notify({ title: this.$tt('加入成功'), content: `${this.$tt('已加入房间')} ${this.syncRoomInput}`, type: 'success', time: 1500 })
            } catch (error) {
                this.$myMsg.notify({ title: this.$tt('加入失败'), content: error.message || this.$tt('加入房间失败'), type: 'error', time: 2000 })
            } finally {
                this.syncBusy = false
            }
        },
        async leaveSyncRoom() {
            if (!this.syncRoomId || this.syncBusy) return
            this.syncBusy = true
            try {
                await this.$store.dispatch('syncRoom/leaveRoom')
                this.$myMsg.notify({ title: this.$tt('已离开房间'), type: 'success', time: 1200 })
            } catch (error) {
                this.$myMsg.notify({ title: this.$tt('离开失败'), content: error.message || this.$tt('离开房间失败'), type: 'error', time: 2000 })
            } finally {
                this.syncBusy = false
            }
        },
        async sendChat() {
            if (!this.syncRoomId || !this.chatInput) return
            try {
                await this.$store.dispatch('syncRoom/sendChat', this.chatInput)
                this.chatInput = ''
            } catch (error) {
                this.$myMsg.notify({ title: this.$tt('发送失败'), content: error.message || this.$tt('消息发送失败'), type: 'error', time: 1500 })
            }
        },
        scrollToLyric(i) {
            if (this.syncReadonly) return
            const line = this.lyricsObjArr[i]
            if (!line) return
            const seek = line.time / (this.duration || 1)
            this.$store.dispatch('seek', seek)
        },
        scrollActiveLyricIntoView(index) {
            this.$nextTick(() => {
                const container = this.$refs.lyricContainer
                if (!container) return
                const lyricRefs = this.$refs.lyric
                const target = Array.isArray(lyricRefs) ? lyricRefs[index] : lyricRefs
                if (!target) return

                const targetCenter = target.offsetTop + (target.offsetHeight / 2)
                const desiredTop = targetCenter - (container.clientHeight / 2)
                const maxTop = Math.max(0, container.scrollHeight - container.clientHeight)
                container.scrollTo({
                    top: Math.max(0, Math.min(desiredTop, maxTop)),
                    behavior: 'smooth',
                })
            })
        },
        formatLyrics(lyrics) {
            if (!lyrics) return []
            const regNewLine = /\n/
            const lineArr = lyrics.split(regNewLine)
            const lyricsObjArr = []
            lineArr.forEach(item => {
                if (item === '') return
                const spiltList = item.split(']')
                const spiltListLength = spiltList.length
                const content = spiltList[spiltListLength - 1].trim()
                const timeArray = spiltList.slice(0, spiltListLength - 1)
                timeArray.forEach(dt => {
                    const obj = {}
                    obj.lyric = content === '' ? '' : content
                    obj.time = this.formatLyricTime(dt.slice(1))
                    obj.uid = Math.random().toString().slice(-6)
                    if (obj.lyric !== '') {
                        lyricsObjArr.push(obj)
                    }
                })
            })
            return lyricsObjArr.sort((a, b) => a.time - b.time)
        },
        formatLyricTime(item) {
            if (item.indexOf('.') === -1) {
                const timeParts = item.split(':')
                const hours = parseInt(timeParts[0], 10)
                const minutes = parseInt(timeParts[1], 10)
                const seconds = parseInt(timeParts[2], 10)
                const totalTime = hours * 60 * 60 + minutes * 60 + seconds
                return Number(totalTime)
            }
            const regTime = /\d{2}:\d{2}.\d{2,3}/
            const isTime = item.match(regTime)
            const time = isTime ? isTime[0] : '00:00.00'
            const regMin = /.*:/
            const regSec = /:.*\./
            const regMs = /\./
            const min = parseInt(time.match(regMin)[0].slice(0, 2), 10)
            let sec = parseInt(time.match(regSec)[0].slice(1, 3), 10)
            const ms = time.slice(time.match(regMs).index + 1, time.match(regMs).index + 3)
            if (min !== 0) {
                sec += min * 60
            }
            return Number(`${sec}.${ms}`)
        },
    },
}
</script>
